import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import "../css/SewMap.css";
import PropTypes from "prop-types";
import { getSewStatusColor } from "../utils/Utils";

// Pans/zooms the map to fit all plotted markers.
const FitBounds = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        if (points.length === 1) {
            map.setView(points[0], 5);
        } else if (points.length > 1) {
            map.fitBounds(points, { padding: [30, 30], maxZoom: 6 });
        }
    }, [points, map]);
    return null;
};

FitBounds.propTypes = {
    points: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired
};

/**
 * An overview map plotting every SEW system that has valid coordinates.
 * Markers are colored by readiness status and link to each system's dossier.
 */
const SewMap = ({ assets, height = 420 }) => {
    const located = useMemo(() => {
        return assets
            .map((asset) => {
                const lat = parseFloat(asset?.Latitude);
                const lon = parseFloat(asset?.Longitude);
                return Number.isFinite(lat) && Number.isFinite(lon) ? { asset, lat, lon } : null;
            })
            .filter(Boolean);
    }, [assets]);

    const points = useMemo(() => located.map(({ lat, lon }) => [lat, lon]), [located]);

    if (located.length === 0) {
        return null;
    }

    return (
        <div
            style={{
                width: "100%",
                height: `${height}px`,
                borderRadius: "5px",
                overflow: "hidden"
            }}
        >
            <MapContainer
                center={[20, 0]}
                zoom={2}
                style={{ width: "100%", height: "100%" }}
                scrollWheelZoom={false}
            >
                <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    attribution="&copy; OpenStreetMap contributors &copy; CARTO"
                    subdomains="abcd"
                />
                <FitBounds points={points} />
                {located.map(({ asset, lat, lon }) => {
                    const { color } = getSewStatusColor(asset?.Status);
                    return (
                        <CircleMarker
                            key={asset.Id}
                            center={[lat, lon]}
                            radius={7}
                            pathOptions={{
                                color: "#ffffff",
                                weight: 1.5,
                                fillColor: color,
                                fillOpacity: 0.9
                            }}
                        >
                            <Tooltip permanent direction="top" offset={[0, -6]} className="sew-tooltip">
                                {asset.Name || "Unknown"}{asset.CountryId ? ` (${asset.CountryId})` : ""}
                            </Tooltip>
                            <Popup>
                                <div style={{ minWidth: "140px" }}>
                                    <strong>{asset.Name}</strong>
                                    <div>Country: {asset.CountryId || "N/A"}</div>
                                    <div>Status: {asset.Status || "N/A"}</div>
                                    <Link to={`/dossier/sew/${asset.Id}`}>View dossier</Link>
                                </div>
                            </Popup>
                        </CircleMarker>
                    );
                })}
            </MapContainer>
        </div>
    );
};

SewMap.propTypes = {
    assets: PropTypes.arrayOf(PropTypes.object).isRequired,
    height: PropTypes.number
};

export default SewMap;
